import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import ErrorBoundary from '@/components/ErrorBoundary';
import { CeoAccountBar, CeoAccountSwitch } from '@/components/CeoAccountSwitch';
import { useAuth } from '@/context/AuthContext';
import ChatWidget from '@/components/ChatWidget';
import BasketPanel from '@/components/BasketPanel';
import CompleteProfilePage from '@/pages/CompleteProfilePage';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { useHideOnScroll } from '@/hooks/useHideOnScroll';
import { ViewAsSelector, ImpersonationBanner } from '@/components/FounderOverride';
import { useCart } from '@/context/CartContext';
import { useRecentPages } from '@/hooks/useRecentPages';
import { isQuoteOnlyDistributor } from '@/lib/quoteOnlyRegions';
import RecentlyVisitedBar from '@/components/RecentlyVisitedBar';
import NotificationBell from '@/components/NotificationBell';
import './salon-luxe.css';
import GlobalSearch from '@/components/GlobalSearch';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, Package, ShoppingCart, ClipboardList, Calculator,
  Users, User, MapPin, LogOut, Menu, X, ChevronRight, Gift, GraduationCap,
  Camera, ClipboardCheck, BookOpen, Star, Info, Warehouse, TrendingUp, Truck, Bell, Award, Trophy, FileText, BarChart3, RefreshCw, Shield, MessageSquare, Repeat, DollarSign, Filter, Calendar, ScanLine, Store, Zap, HelpCircle, LifeBuoy, FileQuestion, Share2, UserPlus, PackageCheck, ToggleLeft, Megaphone, Globe, ChevronDown, Factory, CreditCard, Search, Image as ImageIcon, Heart, Percent, Receipt, Wallet, Banknote, TerminalSquare, CalendarDays
} from 'lucide-react';

import BackButton from '@/components/BackButton';
import { RegionSwitcher } from '@/components/RegionSelector';
import { usePageVisitTracker } from '@/hooks/usePageVisitTracker';
import { useTeamAccess } from '@/hooks/useTeamAccess';
import { TopPillNav } from '@/components/TopPillNav';
import { Calendar as RvkCalendar, Radio as RvkRadio, Mic as RvkMic, Zap as RvkZap, Inbox as RvkInbox } from 'lucide-react';
import ListenBar from '@/components/ListenBar';
import { SalonLuxeMenu } from '@/components/SalonLuxeMenu';
import { watchSections } from '@/pages/booking/sectionVoice';

/* voice_every_section_jun2026 — which blocks count as a "card/section" on RVK Pro salon pages */
const RVK_SV_CARDS = '.bk-card, .card, .rvk-card, .luxe-hero, .achero, [data-testid="salon-stats"], .rvk-roles, .coming-soon-clearance, section.dkp, header.dkp, div[class*="bg-white"][class*="rounded"], div[class*="bg-white"][data-testid]';

import { CeoSalonSwitch } from '@/components/CeoSalonSwitch';

const ADMIN_ROLES = ['founder', 'admin', 'super_admin', 'admin_manager', 'warehouse_admin', 'finance_officer', 'finance_director'];

/* ═══════════════════════════════════════════
   10-ITEM SIDEBAR NAVIGATION — Primary + More ▾
   Max 10 items visible per role. Everything else in More.
   ═══════════════════════════════════════════ */

const adminPrimary = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Quick Order', path: '/dashboard/quick-order', icon: ShoppingCart },
  { label: 'Quotes & Orders', path: '/dashboard/quotes', icon: FileText },
  { label: 'Invoices', path: '/dashboard/uk-invoices', icon: CreditCard },
  { label: 'Clients & Salons', path: '/dashboard/salon-search', icon: Search },
  { label: 'Products', path: '/dashboard/catalogue', icon: Package },
  { label: 'Stock & Warehouse', path: '/dashboard/inventory', icon: Warehouse },
  { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
  { label: 'CEO Command', path: '/dashboard/ceo-command', icon: Zap },
];

const adminMore = [
  // ── UNITED KINGDOM ──
  // sidebar_mirror_19sep2026 (CEO ruling): each region carries its own
  // Invoices entry; All Registrations moved to CEO & General (it is
  // cross-region and does not belong under a UK heading).
  { label: 'United Kingdom', type: 'section' },
  { label: 'UK Salon Orders & Quotes', path: '/dashboard/quotes', icon: FileText },
  { label: 'UK Orders', path: '/dashboard/uk-orders', icon: ClipboardList },
  { label: 'UK Invoices', path: '/dashboard/uk-invoices', icon: CreditCard },
  { label: 'UK Dispatch', path: '/dashboard/uk-dispatch', icon: Truck },
  { label: 'UK Warehouse', path: '/dashboard/uk-warehouse', icon: Warehouse },
  { label: 'UK Distributors', path: '/dashboard/uk-distributors', icon: Users },
  { label: 'UK Salons', path: '/dashboard/uk-salons', icon: Store },

  // ── NIGERIA ──
  // sidebar_mirror_19sep2026: mirrors the UK order. No Nigeria Dispatch
  // item — no NG dispatch page exists (dispatch is an action inside the
  // Nigeria Orders drilldowns). No Nigeria Distributors register exists —
  // only the queue below. Warehouse is Nigeria-scoped via the stock
  // locations page's region filter (stock_locations.region = 'nigeria'),
  // never the shared inventory page.
  { label: 'Nigeria', type: 'section' },
  { label: 'Nigeria Orders & Quotes', path: '/dashboard/orders', icon: ClipboardList },
  { label: 'Nigeria Invoices', path: '/dashboard/ng-invoices', icon: CreditCard },
  { label: 'Nigeria Dispatch', path: '/dashboard/ng-dispatch-history', icon: Truck },
  { label: 'Nigeria Warehouse', path: '/dashboard/stock?region=nigeria', icon: Warehouse },
  { label: 'Nigeria Salons', path: '/dashboard/salon-registry', icon: Store },
  { label: 'Nigeria Salon Queue', path: '/dashboard/ng-salon-queue', icon: Users },
  { label: 'Nigeria Distributor Queue', path: '/dashboard/onboarding?region=nigeria', icon: Users },

  // ── CEO / GENERAL ──
  { label: 'CEO & General', type: 'section' },
  { label: 'All Registrations', path: '/dashboard/salon-pipeline', icon: Users },
  { label: 'Order Queue', path: '/dashboard/order-queue', icon: ClipboardList },
  { label: 'Warehouse Overview', path: '/dashboard/warehouse-overview', icon: Warehouse },
  { label: 'Team Performance', path: '/dashboard/team-performance', icon: BarChart3 },
  { label: 'CEO Digest', path: '/dashboard/ceo-digest', icon: BarChart3 },
  { label: 'Sales Analytics', path: '/dashboard/sales-analytics', icon: TrendingUp },
  { label: 'Log a Sale', path: '/dashboard/sales-log', icon: DollarSign },
  { label: 'Invoices', path: '/dashboard/invoices', icon: FileText },
  { label: 'Products', path: '/dashboard/catalogue', icon: Package },
  { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
  { label: 'Staff Directory', path: '/dashboard/staff', icon: Users },

  // ── TOOLS & SETTINGS ──
  { label: 'Tools & Settings', type: 'section' },
  { label: 'Professionals', path: '/dashboard/salon-directory', icon: Globe },
  { label: 'Stock Intelligence', path: '/dashboard/inventory-intelligence', icon: TrendingUp },
  { label: 'Rep Alerts', path: '/dashboard/rep-alerts', icon: Bell },
  { label: 'Stock Setup', path: '/dashboard/stock-setup', icon: PackageCheck },
  { label: 'Stock Control', path: '/dashboard/stock', icon: Package },
  { label: 'Salon Performance', path: '/dashboard/salon-performance', icon: Users },
  { label: 'Barcode Scanner', path: '/dashboard/barcode', icon: ScanLine },
  { label: 'Leaderboard', path: '/dashboard/leaderboard', icon: Trophy },
  { label: 'Sales Funnel', path: '/dashboard/sales-funnel', icon: Filter },
  { label: 'WhatsApp', path: '/dashboard/whatsapp', icon: MessageSquare },
  { label: 'Leads', path: '/dashboard/leads', icon: UserPlus },
  { label: 'Subscriptions', path: '/dashboard/subscriptions', icon: Repeat },
  { label: 'Starter Packs', path: '/dashboard/starter-packs', icon: Gift },
  { label: 'Share Pack', path: '/dashboard/share-pack', icon: Share2 },
  { label: 'Cart', path: '/dashboard/cart', icon: ShoppingCart },
  { label: 'Learning', path: '/dashboard/learning', icon: GraduationCap },
  { label: 'Partner Rewards', path: '/dashboard/rewards', icon: Star },
  { label: 'Business Planner', path: '/dashboard/business-planner', icon: BarChart3 },
  { label: 'Calculator', path: '/dashboard/calculator', icon: Calculator },
  { label: 'Master Dashboard', path: '/dashboard/master', icon: LayoutDashboard },
  { label: 'Stock Check', path: '/dashboard/stock-check', icon: ClipboardCheck },
  { label: 'Factory Orders', path: '/dashboard/factory-orders', icon: Factory },
  { label: 'Margins & Costs', path: '/dashboard/cost-dashboard', icon: TrendingUp },
  { label: 'Finance', path: '/dashboard/finance', icon: CreditCard },
  { label: 'Stock Transfers', path: '/dashboard/stock-transfers', icon: Truck },
  { label: 'Transfer Requests', path: '/dashboard/transfer-requests', icon: Truck },
  { label: 'Distributor Applications Queue', path: '/dashboard/onboarding', icon: UserPlus },
  { label: 'Salon Onboarding Queue', path: '/dashboard/salon-onboarding', icon: UserPlus },
  { label: 'New Account Onboarding', path: '/dashboard/onboard', icon: UserPlus },
  { label: 'Salon Teams', path: '/dashboard/salon-teams', icon: Users },
  { label: 'Discount Control', path: '/dashboard/discount-control', icon: Percent },
  { label: 'Follow-Up Automation', path: '/dashboard/automation', icon: Zap },
  { label: 'WhatsApp Automation', path: '/dashboard/whatsapp-automation', icon: MessageSquare },
  { label: 'WhatsApp Inbox', path: '/dashboard/whatsapp-inbox', icon: MessageSquare },
  { label: 'Demo Invites', path: '/dashboard/demo-invites', icon: Share2 },
  { label: 'Thank You Messages', path: '/dashboard/thank-you', icon: Heart },
  { label: 'Rep Stock Control', path: '/dashboard/stock-reps', icon: ClipboardCheck },
  { label: 'Security Log', path: '/dashboard/security-log', icon: Shield },
  { label: 'Start Here', path: '/dashboard/start', icon: HelpCircle },
  { label: 'Homepage Editor', path: '/dashboard/editor/homepage', icon: Globe },
  { label: 'Visit Tracking', path: '/dashboard/visits', icon: ClipboardCheck },
  { label: 'Weekly Digest', path: '/dashboard/weekly-digest', icon: Calendar },
  { label: 'Payroll', path: '/dashboard/payroll', icon: DollarSign },
  { label: 'Moderation', path: '/dashboard/moderation', icon: Shield },
  { label: 'Media Review', path: '/dashboard/media-review', icon: ImageIcon },
  { label: 'Bulletin Board', path: '/dashboard/bulletins', icon: Megaphone },
  { label: 'Feature Toggles', path: '/dashboard/feature-toggles', icon: ToggleLeft },
  { label: 'Newsletter', path: '/dashboard/marketing/newsletter', icon: FileText },
  { label: 'Payment Review', path: '/dashboard/payment-review', icon: CreditCard },
  { label: 'Notifications', path: '/dashboard/messages', icon: Bell },
  { label: 'FAQ', path: '/dashboard/faq', icon: FileQuestion },
  { label: 'Help & Support', path: '/dashboard/help', icon: LifeBuoy },
];

const salesRepPrimary = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Quick Order', path: '/dashboard/quick-order', icon: ShoppingCart },
  { label: 'My Clients', path: '/dashboard/salon-registry', icon: Store },
  { label: 'Quotes', path: '/dashboard/quotes', icon: FileText },
  { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
  { label: 'Learning', path: '/dashboard/learning', icon: GraduationCap },
  { label: 'Products', path: '/dashboard/catalogue', icon: Package },
  { label: 'My Orders', path: '/dashboard/orders', icon: ClipboardList },
  { label: 'My Invoices', path: '/dashboard/my-invoices', icon: Receipt },
  { label: 'Commission', path: '/dashboard/my-payroll', icon: DollarSign },
  { label: 'Receipts', path: '/dashboard/my-receipts', icon: CreditCard },
  // ceo_instruction_ref: rep_nav_ng_bulk_payments_16aug2026
  { label: 'Record Payment', path: '/dashboard/ng-invoices', icon: Banknote },
];

const salesRepMore = [
  { label: 'Cart', path: '/dashboard/cart', icon: ShoppingCart },
  { label: 'Barcode Scanner', path: '/dashboard/barcode', icon: ScanLine },
  { label: 'Scan & Reorder', path: '/dashboard/product-scanner', icon: Camera },
  { label: 'Finish Product', path: '/finish-product', icon: PackageCheck },
  { label: 'Sales Funnel', path: '/dashboard/sales-funnel', icon: Filter },
  { label: 'Share Pack', path: '/dashboard/share-pack', icon: Share2 },
  { label: 'Leads', path: '/dashboard/leads', icon: UserPlus },
  { label: 'Visit Tracking', path: '/dashboard/visits', icon: ClipboardCheck },
  { label: 'Command Centre', path: '/dashboard/command-centre', icon: BarChart3 },
  { label: 'Business Planner', path: '/dashboard/business-planner', icon: BarChart3 },
  { label: 'Calculator', path: '/dashboard/calculator', icon: Calculator },
  { label: 'Starter Packs', path: '/dashboard/starter-packs', icon: Gift },
  { label: 'Partner Rewards', path: '/dashboard/rewards', icon: Star },
  { label: 'Professionals', path: '/dashboard/salon-directory', icon: Globe },
  { label: 'Leaderboard', path: '/dashboard/leaderboard', icon: Trophy },
  { label: 'Stock Check', path: '/dashboard/stock-check-rep', icon: ClipboardCheck },
  { label: 'Transfer Requests', path: '/dashboard/transfer-requests', icon: Truck },
  { label: 'Notifications', path: '/dashboard/messages', icon: Bell },
  { label: 'FAQ', path: '/dashboard/faq', icon: FileQuestion },
  { label: 'Help & Support', path: '/dashboard/help', icon: LifeBuoy },
];

const salonPrimary = [];
const salonMore = [];

/* ═══ SALON GROUPED NAV — 6 accordion groups ═══ */
const salonGroupedNav = [
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
  },
  {
    label: 'Shop',
    icon: ShoppingCart,
    children: [
      { label: 'Place Order', path: '/dashboard/catalogue', icon: Package },
      { label: 'Cart', path: '/dashboard/cart', icon: ShoppingCart },
      { label: 'Starter Packs', path: '/dashboard/starter-packs', icon: Gift },
    ],
  },
  {
    label: 'Orders & Billing',
    icon: CreditCard,
    children: [
      { label: 'My Orders', path: '/dashboard/my-salon-orders', icon: ClipboardList },
      { label: 'Invoices', path: '/dashboard/my-salon-invoices', icon: CreditCard },
      { label: 'My Quotes', path: '/dashboard/my-salon-quotes', icon: FileText },
    ],
  },
  {
    label: 'Learn',
    icon: GraduationCap,
    children: [
      { label: 'Learning', path: '/dashboard/learning', icon: GraduationCap },
      { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
      { label: 'Results', path: '/dashboard/results-logbook', icon: Camera },
    ],
  },
  {
    label: 'Grow',
    icon: TrendingUp,
    children: [
      { label: 'My Team', path: '/dashboard/my-team', icon: Users },
      { label: 'Business Planner', path: '/dashboard/business-planner', icon: BarChart3 },
      { label: 'Calculator', path: '/dashboard/calculator', icon: Calculator },
      { label: 'Partner Rewards', path: '/dashboard/rewards', icon: Star },
      { label: 'Professionals', path: '/dashboard/salon-directory', icon: Globe },
    ],
  },
  {
    label: 'Restock',
    icon: RefreshCw,
    children: [
      { label: 'Scan & Reorder', path: '/dashboard/product-scanner', icon: ScanLine },
      { label: 'Auto-Restock', path: '/dashboard/subscriptions', icon: Repeat },
      { label: 'Reorder', path: '/dashboard/reorder', icon: RefreshCw },
      { label: 'Smart Restock', path: '/dashboard/reorder-intelligence', icon: TrendingUp },
      { label: 'Stock Check', path: '/dashboard/stock-check-rep', icon: ClipboardCheck },
    ],
  },
];

const salonFooterItems = [
  { label: 'Profile', path: '/dashboard/profile', icon: User },
  { label: 'Notifications', path: '/dashboard/messages', icon: Bell },
  { label: 'FAQ', path: '/dashboard/faq', icon: FileQuestion },
  { label: 'Help', path: '/dashboard/help', icon: LifeBuoy },
];

/* Legacy nav arrays for warehouse, delivery, distributor, team, demo — kept as-is */
const warehouseAdminNav = [
  { group: 'Core', items: [
    { label: 'Dispatch', path: '/dashboard/warehouse', icon: Truck },
    { label: 'Dispatch History', path: '/dashboard/dispatch-history', icon: ClipboardList },
    { label: 'Inventory', path: '/dashboard/inventory', icon: Warehouse },
    { label: 'Stock Check', path: '/dashboard/stock-check', icon: ClipboardCheck },
    { label: 'Barcode Scanner', path: '/dashboard/barcode', icon: ScanLine },
    { label: 'Notifications', path: '/dashboard/messages', icon: Bell },
  ]},
];

const deliveryAgentNav = [
  { group: 'Core', items: [
    { label: 'My Deliveries', path: '/dashboard', icon: Truck },
    { label: 'Notifications', path: '/dashboard/messages', icon: Bell },
    { label: 'FAQ', path: '/dashboard/faq', icon: FileQuestion },
    { label: 'Help & Support', path: '/dashboard/help', icon: LifeBuoy },
  ]},
];

const distributorNav = [
  { group: 'Core', items: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Place Order', path: '/dashboard/distributor', icon: ShoppingCart },
    { label: 'Products', path: '/dashboard/catalogue', icon: Package },
  ]},
  { group: 'Business', items: [
    { label: 'My Orders', path: '/dashboard/my-salon-orders', icon: ClipboardList },
    { label: 'My Invoices', path: '/dashboard/my-salon-invoices', icon: CreditCard },
    { label: 'My Quotes', path: '/dashboard/my-salon-quotes', icon: FileText },
  ]},
  { group: 'Tools', items: [
    { label: 'Calculator', path: '/dashboard/calculator', icon: Calculator },
    { label: 'Partner Rewards', path: '/dashboard/rewards', icon: Star },
    { label: 'Learning', path: '/dashboard/learning', icon: GraduationCap },
    { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
    { label: 'Distributor Packs', path: '/dashboard/distributor-packs', icon: Package },
    { label: 'Resources', path: '/dashboard/resources', icon: FileText },
    { label: 'Notifications', path: '/dashboard/messages', icon: Bell },
    { label: 'FAQ', path: '/dashboard/faq', icon: FileQuestion },
    { label: 'Help & Support', path: '/dashboard/help', icon: LifeBuoy },
  ]},
];

const teamMemberNav = [
  { group: 'Core', items: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'About Revitalise', path: '/dashboard/about', icon: Info },
    { label: 'Shop Home Care', path: '/dashboard/catalogue', icon: Package },
    { label: 'Cart', path: '/dashboard/cart', icon: ShoppingCart },
    { label: 'My Orders', path: '/dashboard/orders', icon: ClipboardList },
    { label: 'Learning', path: '/dashboard/learning', icon: GraduationCap },
    { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
    { label: 'Notifications', path: '/dashboard/messages', icon: Bell },
    { label: 'FAQ', path: '/dashboard/faq', icon: FileQuestion },
    { label: 'Help & Support', path: '/dashboard/help', icon: LifeBuoy },
  ]},
];

const demoSalonNav = [
  { group: 'Core', items: [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Products', path: '/dashboard/catalogue', icon: Package },
    { label: 'Scan & Reorder', path: '/dashboard/product-scanner', icon: ScanLine },
    { label: 'Learning', path: '/dashboard/learning', icon: GraduationCap },
    { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
  ]},
  { group: 'Business', items: [
    { label: 'My Orders', path: '/dashboard/my-salon-orders', icon: ClipboardList },
    { label: 'My Invoices', path: '/dashboard/my-salon-invoices', icon: CreditCard },
    { label: 'My Quotes', path: '/dashboard/my-salon-quotes', icon: FileText },
    { label: 'Starter Packs', path: '/dashboard/starter-packs', icon: Gift },
    { label: 'Calculator', path: '/dashboard/calculator', icon: Calculator },
  ]},
  { group: 'Support', items: [
    { label: 'FAQ', path: '/dashboard/faq', icon: FileQuestion },
    { label: 'Help & Support', path: '/dashboard/help', icon: LifeBuoy },
  ]},
];


/* ═══════════════════════════════════════════
   FINANCE DIRECTOR TAB NAV — every dashboard tab, with icons
   ceo_instruction_ref: FINANCE-SIDEBAR-COMPLETE-03JUL2026

   Sidebar for role === 'finance_director' only. Each link
   writes ?tab=<TabName> via history.pushState and dispatches a
   popstate event so the dashboard's popstate listener swaps the
   active tab without a route remount. Active state is computed
   from the current ?tab= search param. No other role affected.
   Active style per spec: 3px gold left border + gold #B8963E +
   background #FEF9F0. Inactive: muted #7A8A96, no border.
   Order matches the top tab strip on NickyFinanceDashboard.js
   with Kenya positioned between Factory and Stock per CEO.
   ═══════════════════════════════════════════ */
const FINANCE_DIRECTOR_TABS = [
  { label: 'Overview',    icon: LayoutDashboard },
  { label: 'Invoices',    icon: Receipt },
  { label: 'Payments',    icon: CreditCard },
  { label: 'Dispatch',    icon: Truck },
  { label: 'Clients',     icon: Users },
  { label: 'P&L',         icon: FileText },
  { label: 'Factory',     icon: Factory },
  { label: 'Kenya',       icon: Globe },
  { label: 'Stock',       icon: Package },
  { label: 'Regions',     icon: MapPin },
  { label: 'Performance', icon: BarChart3 },
  { label: 'Packs',       icon: PackageCheck },
  { label: 'Returns',     icon: Repeat },
  { label: 'Expenses',    icon: Wallet },
];

function FinanceDirectorTabNav({ location, setSidebarOpen }) {
  const FD_PATH = '/dashboard/finance-director';
  const onFdRoute = location.pathname === FD_PATH;
  const currentTab = (() => {
    try {
      const t = new URLSearchParams(location.search).get('tab');
      return t || 'Overview';
    } catch { return 'Overview'; }
  })();
  const handleClick = (e, tab) => {
    e.preventDefault();
    setSidebarOpen(false);
    try {
      const u = new URL(window.location.href);
      u.pathname = FD_PATH;
      u.searchParams.set('tab', tab);
      window.history.pushState({}, '', u.toString());
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch { /* noop */ }
  };
  return (
    <div className="space-y-0.5" data-testid="finance-director-sidebar">
      {FINANCE_DIRECTOR_TABS.map(({ label, icon: Icon }) => {
        const isActive = onFdRoute && currentTab === label;
        const activeStyle = {
          color: '#B8963E',
          background: '#FEF9F0',
          borderLeft: '3px solid #B8963E',
          paddingLeft: '13px',
        };
        const inactiveStyle = {
          color: '#7A8A96',
          background: 'transparent',
          borderLeft: '3px solid transparent',
          paddingLeft: '13px',
        };
        return (
          <a
            key={label}
            href={`${FD_PATH}?tab=${encodeURIComponent(label)}`}
            onClick={(e) => handleClick(e, label)}
            className="flex items-center px-4 py-2 text-[13px] font-medium rounded-lg mx-1 transition-all"
            style={isActive ? activeStyle : inactiveStyle}
            data-testid={`fd-sidebar-${label.toLowerCase().replace(/[^a-z]/g, '')}`}
          >
            <Icon className="h-4 w-4 mr-3 flex-shrink-0" />
            <span className="truncate">{label}</span>
          </a>
        );
      })}
      {/* agent_commission_engine_phase2_14oct2026: Nicky sees due/paid and
          can Mark Paid — no rate changes (enforced server-side). */}
      <a href="/dashboard/agent-commissions"
        onClick={(e) => { e.preventDefault(); setSidebarOpen(false); window.history.pushState({}, '', '/dashboard/agent-commissions'); window.dispatchEvent(new PopStateEvent('popstate')); }}
        className="flex items-center px-4 py-2 text-[13px] font-medium rounded-lg mx-1 transition-all"
        style={{ color: '#7A8A96', borderLeft: '3px solid transparent', paddingLeft: '13px' }}
        data-testid="fd-sidebar-agentcommissions">
        <Percent className="h-4 w-4 mr-3 flex-shrink-0" />
        <span className="truncate">Agent Commissions</span>
      </a>
    </div>
  );
}

/* ═══════════════════════════════════════════
   SIDEBAR NAV ITEM COMPONENT
   ═══════════════════════════════════════════ */
function NavItem({ item, location, totalItems, setSidebarOpen }) {
  const isActive = location.pathname === item.path || (item.altPath && location.pathname === item.altPath);
  // Same-path remount guard (ceo_instruction_ref:
  //   dashboard_flash_burger_same_path_fix_30may2026):
  // when the burger nav is tapped on the route the user is
  // already on, swallow the Link navigation so React Router
  // does not discard and remount the current page component.
  // Only the sidebar close action runs.
  const handleClick = (e) => {
    if (location.pathname === item.path) {
      e.preventDefault();
      setSidebarOpen(false);
      return;
    }
    setSidebarOpen(false);
  };
  return (
    <Link key={item.path} to={item.path}
      className={`flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium transition-all rounded-lg mx-1 ${
        isActive
          ? 'bg-[#B99A65]/10 text-[#B99A65] border-l-2 border-[#B99A65] pl-3.5'
          : 'text-[#4A4A4A] hover:text-[#2B1F27] hover:bg-[#E6F0FA]'
      }`}
      onClick={handleClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/[&\s]+/g, '-')}`}>
      <item.icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{item.label}</span>
      {item.label === 'Cart' && totalItems > 0 && (
        <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="sidebar-cart-badge">{totalItems}</span>
      )}
    </Link>
  );
}

/* ═══════════════════════════════════════════
   "MORE" EXPANDABLE SECTION
   ═══════════════════════════════════════════ */
function MoreSection({ items, location, totalItems, setSidebarOpen }) {
  const [expanded, setExpanded] = useState(false);
  const navItems = items.filter(i => !i.type);
  const hasActiveChild = navItems.some(item => location.pathname === item.path);

  // Auto-expand if active page is in More
  useEffect(() => {
    if (hasActiveChild) setExpanded(true);
  }, [hasActiveChild]);

  return (
    <div className="mt-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium transition-all rounded-lg mx-1 ${
          hasActiveChild && !expanded
            ? 'text-[#B99A65]'
            : 'text-[#4A4A4A] hover:text-[#2B1F27] hover:bg-[#E6F0FA]'
        }`}
        data-testid="nav-more-toggle"
      >
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        <span className="truncate">More</span>
      </button>
      {expanded && (
        <div className="mt-0.5 pl-1 border-l border-[#E3EAF3] ml-5">
          {items.map((item, i) => {
            if (item.type === 'section') {
              return (
                <div key={`section-${i}`} className="pt-3 pb-1 px-3 first:pt-1">
                  <div className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#B99A65]" data-testid={`nav-section-${item.label.toLowerCase().replace(/[&\s]+/g, '-')}`}>
                    {item.label}
                  </div>
                </div>
              );
            }
            return <NavItem key={item.path} item={item} location={location} totalItems={totalItems} setSidebarOpen={setSidebarOpen} />;
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   ACCORDION NAV GROUP — for salon 6-item structure
   ═══════════════════════════════════════════ */
function AccordionNavGroup({ group, location, totalItems, setSidebarOpen }) {
  const [expanded, setExpanded] = useState(false);
  const children = group.children || [];
  const hasChildren = children.length > 0;
  const hasActiveChild = children.some(item => location.pathname === item.path);

  useEffect(() => {
    if (hasActiveChild) setExpanded(true);
  }, [hasActiveChild]);

  // Direct link (e.g. Dashboard) — no children, render as simple NavItem
  if (!hasChildren) {
    return <NavItem item={group} location={location} totalItems={totalItems} setSidebarOpen={setSidebarOpen} />;
  }

  const Icon = group.icon;
  const cartBadge = group.label === 'Shop' && totalItems > 0;

  return (
    <div className="mb-0.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-semibold transition-all rounded-lg mx-1 ${
          hasActiveChild
            ? 'bg-[#B99A65]/10 text-[#B99A65]'
            : 'text-[#4A4A4A] hover:text-[#2B1F27] hover:bg-[#E6F0FA]'
        }`}
        data-testid={`nav-group-${group.label.toLowerCase().replace(/[&\s]+/g, '-')}`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="truncate flex-1 text-left">{group.label}</span>
        {cartBadge && (
          <span className="bg-[#B99A65] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{totalItems}</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="space-y-0.5 mt-0.5 pl-3 ml-5 border-l border-[#E3EAF3]">
          {children.map(item => (
            <NavItem key={item.path} item={item} location={location} totalItems={totalItems} setSidebarOpen={setSidebarOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   FOOTER ICON ROW — Profile, Notifications, FAQ, Help
   ═══════════════════════════════════════════ */
function FooterIconRow({ items, location, setSidebarOpen }) {
  return (
    <div className="flex items-center justify-around px-2 py-2 border-t border-[#E3EAF3]" data-testid="footer-icon-row">
      {items.map(item => {
        const isActive = location.pathname === item.path;
        const Icon = item.icon;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setSidebarOpen(false)}
            className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition-colors ${
              isActive ? 'text-[#B99A65]' : 'text-[#64525D] hover:text-[#2B1F27]'
            }`}
            data-testid={`footer-${item.label.toLowerCase()}`}
          >
            <Icon className="w-4 h-4" />
            <span className="text-[9px] font-medium">{item.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════
   LEGACY GROUP COMPONENT (for warehouse, delivery, distributor, etc.)
   ═══════════════════════════════════════════ */
function NavGroup({ group, items, location, totalItems, setSidebarOpen }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="mb-1">
      {group !== 'Core' && (
        <button onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-between px-3 py-1.5 text-[9px] uppercase tracking-[0.12em] font-semibold text-[#64525D] hover:text-[#4A4A4A] transition-colors"
          data-testid={`nav-group-${group.toLowerCase()}`}>
          <span>{group}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        </button>
      )}
      {!collapsed && (
        <div className="space-y-0.5">
          {items.map(item => (
            <NavItem key={item.path} item={item} location={location} totalItems={totalItems} setSidebarOpen={setSidebarOpen} />
          ))}
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════
   MAIN LAYOUT
   ═══════════════════════════════════════════ */
export default function DashboardLayout() {
  // TEMP diagnostic — render trace
  // ceo_instruction_ref: dashboard_layout_render_trace_22jun2026
  console.log('[render-trace] DashboardLayout render fired', new Date().toISOString());
  const { user, token, logout, impersonation } = useAuth();
  const { totalItems } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    /* h305_fixes item 8: ☰ inside the booking iframe opens the main menu */
    const onMsg = (e) => { if (e.data && e.data.rvk === 'openMenu') setSidebarOpen(true); };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);
  /* phone_fullscreen_jun2026 — slim 50px bar: search opens on 🔍, Listen lives in the bar */
  const [searchOpen, setSearchOpen] = useState(false);
  const [barSpeak, setBarSpeak] = useState('off');
  // FINAL_23 item 2: the design top bar's Aa text-size control, everywhere
  const [aa, setAa] = useState(() => Number(localStorage.getItem('rvk_aa') || 0));
  useEffect(() => {
    document.documentElement.style.fontSize = ['16px', '18px', '20px'][aa] || '16px';
    localStorage.setItem('rvk_aa', String(aa));
  }, [aa]);
  const cycleAa = () => setAa((v) => (v + 1) % 3);
  // FINAL_25 speed: prefetch the booking app in the background once the
  // shell is idle, so Calendar opens from cache even on slow connections.
  useEffect(() => {
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 4000));
    idle(() => {
      ['/bookapp/app_core.js?v=06jun2', '/bookapp/index.html'].forEach((u) => {
        fetch(u, { credentials: 'same-origin' }).catch(() => {});
      });
    });
  }, []);
  const barsHidden = useHideOnScroll();
  const barListen = () => {
    const s = window.speechSynthesis;
    if (!s) return;
    if (barSpeak === 'on') { s.pause(); setBarSpeak('paused'); return; }
    if (barSpeak === 'paused') { s.resume(); setBarSpeak('on'); return; }
    const text = (document.querySelector('main')?.innerText || '').replace(/\s+/g, ' ').slice(0, 6000);
    if (!text) return;
    s.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-GB';
    u.onend = () => setBarSpeak('off');
    s.speak(u);
    setBarSpeak('on');
  };
  const barStop = () => { window.speechSynthesis?.cancel(); setBarSpeak('off'); };
  /* ceo_bottom_bar_27oct2026 — the ☰ menu opens over everything (above the
     plum bottom bar); Escape closes it; floating Listen/chat hide via the
     body class while any menu or sheet is open. */
  useEffect(() => {
    document.body.classList.toggle('rvk-menu-open', sidebarOpen);
    if (!sidebarOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('rvk-menu-open');
    };
  }, [sidebarOpen]);
  // academy_exit_button_21oct2026 — the Academy burger hands over here:
  // open the main menu drawer on arrival (drawer mode below xl).
  useEffect(() => {
    if (sessionStorage.getItem('rk_open_main_menu') === '1') {
      sessionStorage.removeItem('rk_open_main_menu');
      setSidebarOpen(true);
    }
  }, []);
  useRecentPages();
  usePageVisitTracker();
  const teamAccess = useTeamAccess();

  const role = user?.role || 'salon';
  // team_experience_26oct2026 — invited salon_staff (below manager) get the
  // same gated team experience as team_member.
  const isTeamExperience = role === 'team_member'
    || (role === 'salon_staff' && !['owner', 'manager'].includes((user?.salon_role || '').toLowerCase()));
  // reskin_batch1_26oct2026 — the whole portal carries the plum/cream/gold
  // one-look shell (CEO: header, search, menu, side menu, background).
  const luxeShell = role !== '__never__';
  const isAdmin = ADMIN_ROLES.includes(role);

  // ceo_instruction_ref: REGION-ARCHITECTURE-PHASE1-REFACTOR-01JUL2026
  // Active-region probe drives the distributor sidebar injection so
  // any region whose features.my_products AND features.campaign_images
  // are true gets the My Products / Campaign Images links, not just Kenya.
  const [activeRegions, setActiveRegions] = useState(null);
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backendUrl}/api/finance-director/regions/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setActiveRegions(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setActiveRegions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Mark body as authenticated so global CSS can hide marketing elements (e.g. Emergent badge).
  // Badge is injected by emergent-main.js asynchronously — use interval + MutationObserver
  // to catch every insertion/re-insertion attempt.
  useEffect(() => {
    document.body.classList.add('rk-authenticated');
    const hideBadge = () => {
      const b = document.getElementById('emergent-badge');
      if (b) {
        b.style.display = 'none';
        b.style.visibility = 'hidden';
        b.setAttribute('aria-hidden', 'true');
        return true;
      }
      return false;
    };
    // Immediate + retry for 10 seconds (async badge injection)
    hideBadge();
    const interval = setInterval(() => {
      hideBadge();
    }, 300);
    const safety = setTimeout(() => clearInterval(interval), 10000);
    // Re-hide if the badge is re-inserted later
    const observer = new MutationObserver(() => { hideBadge(); });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      clearInterval(interval);
      clearTimeout(safety);
      observer.disconnect();
      document.body.classList.remove('rk-authenticated');
      const b = document.getElementById('emergent-badge');
      if (b) {
        b.style.display = '';
        b.style.visibility = '';
        b.removeAttribute('aria-hidden');
      }
    };
  }, []);

  // Demo user: pick nav based on the view they were invited to see
  const demoViewAs = user?.demo_view_as;
  const demoVisibility = user?.visibility;

  const isSalonRole = ['salon', 'salon_owner', 'salon_manager', 'salon_staff', 'professional', 'salon_business', 'professional_hairdresser'].includes(role);
  const usesAccordionNav = isSalonRole;

  /* one_menu_01nov2026 — ONE menu payload for the whole platform; also
     drives the after-login Home (booking Home for salons with booking on). */
  const [oneMenu, setOneMenu] = useState(null);
  useEffect(() => {
    if (!token || !isSalonRole) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bk/one-menu`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setOneMenu(data);
      } catch { /* hidden by default */ }
    })();
    return () => { cancelled = true; };
  }, [token, isSalonRole]);
  const bookingNavOn = !!oneMenu?.booking;
  /* platform_menu_reskin_01nov2026 — Home after login is the booking Home
     for salons with booking on; the RVK dashboard for everyone else. */
  useEffect(() => {
    if (isSalonRole && oneMenu?.booking && location.pathname === '/dashboard') {
      navigate('/dashboard/booking/home', { replace: true });
    }
  }, [isSalonRole, oneMenu, location.pathname, navigate]);

  const salonNavWithBooking = bookingNavOn
    ? [salonGroupedNav[0], { label: 'Salon booking', path: '/booking', icon: CalendarDays }, ...salonGroupedNav.slice(1)]
    : salonGroupedNav;

  /* Determine if this role uses primary/more pattern or legacy groups */
  const usesPrimaryMore = !['warehouse_admin', 'delivery_agent', 'distributor', 'team_member'].includes(role)
    && !(role === 'demo_user')
    && !usesAccordionNav;

  let navPrimary = [];
  let navMoreItems = [];
  let navGroups = [];

  if (role === 'demo_user') {
    navGroups = demoViewAs === 'distributor' ? distributorNav
      : demoViewAs === 'sales_rep' ? [{ group: 'Core', items: [...salesRepPrimary, ...salesRepMore] }]
      : demoViewAs === 'founder' ? [{ group: 'Core', items: [...adminPrimary, ...adminMore] }]
      : demoSalonNav;
  } else if (isAdmin) {
    if (role === 'warehouse_admin') {
      navGroups = warehouseAdminNav;
    } else if (role === 'finance_officer') {
      navPrimary = [
        { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { label: 'Finance', path: '/dashboard/finance', icon: CreditCard },
        { label: 'Margins & Costs', path: '/dashboard/cost-dashboard', icon: TrendingUp },
      ];
      navMoreItems = [];
    } else if (role === 'finance_director') {
      // Nicky Campbell — Phase 2 dashboard with in-page tabs
      // driven by ?tab=… URL params. Sidebar rendering for
      // this role is handled by <FinanceDirectorTabNav/> below
      // (see <nav> branch), so navPrimary stays empty here to
      // short-circuit the default NavItem rendering.
      // ceo_instruction_ref:
      //   nicky_dashboard_final_complete_28jun2026
      navPrimary = [];
      navMoreItems = [];
    } else {
      navPrimary = adminPrimary;
      navMoreItems = adminMore;
      // ceo_instruction_ref: CAMPAIGN-IMAGES-SIDEBAR-FD-01JUL2026
      // Founder-only injection of the Campaign Images link into the
      // More section — deliberately gated on role === 'founder' so it
      // does NOT leak to admin, super_admin, admin_manager,
      // warehouse_admin or finance_officer (all of whom also fall
      // into this else branch). A fresh array is built to avoid
      // mutating the module-level adminMore constant.
      if (role === 'founder') {
        // ng_ceo_sidebar_links_23sep2026 (CEO order): the two CEO-only
        // Nigeria screens surface under the Nigeria section for the
        // founder ONLY — same non-leak pattern as Campaign Images.
        const withNgCeoLinks = [...adminMore];
        const ngInsertAt = withNgCeoLinks.findIndex(i => i.label === 'Nigeria Distributor Queue') + 1;
        withNgCeoLinks.splice(ngInsertAt, 0,
          { label: 'Nigeria USD Pricing', path: '/dashboard/ng-usd-pricing', icon: DollarSign },
          { label: 'Nigeria Payment Accounts', path: '/dashboard/ng-payment-accounts', icon: CreditCard },
        );
        navMoreItems = [
          ...withNgCeoLinks,
          // academy_ceo_menu_20oct2026 — founder ONLY, same non-leak
          // pattern as Campaign Images. Opens the Academy.
          { label: 'Academy', path: '/academy', icon: GraduationCap },
          // booking_platform_build_01nov2026_go — founder entry, shipped dark
          { label: 'Salon Booking', path: '/booking', icon: GraduationCap },
          { label: 'Campaign Images', path: '/dashboard/campaign-images', icon: ImageIcon },
          { label: 'Agent Commissions', path: '/dashboard/agent-commissions', icon: Percent },
          // welcome_email_prod_test_28sep2026 — founder ONLY, same
          // non-leak pattern as Campaign Images.
          { label: 'Welcome Campaign', path: '/dashboard/welcome-campaign', icon: Megaphone },
          // run_migration_console_29sep2026 — founder ONLY, same
          // non-leak pattern as Welcome Campaign.
          { label: 'Run Migration', path: '/dashboard/run-migration', icon: TerminalSquare },
        ];
      }
    }
  } else if (role === 'delivery_agent') {
    navGroups = deliveryAgentNav;
  } else if (role === 'sales_rep') {
    navPrimary = salesRepPrimary;
    navMoreItems = salesRepMore;
  } else if (role === 'distributor') {
    // ceo_instruction_ref: kenya_my_products_nav_fix_30jun2026
    // Inject "My Products" directly below the "Products" link in
    // the Core group for Kenya region distributors only.
    // ceo_instruction_ref: REGION-ARCHITECTURE-PHASE1-REFACTOR-01JUL2026
    // Injection now driven by region_registry.features rather than
    // a hardcoded "kenya" string — any active region whose row has
    // features.my_products and/or features.campaign_images true
    // gets the respective sidebar links.
    // ceo_instruction_ref: CAMPAIGN-IMAGES-ALL-DISTRIBUTORS-ROLLOUT-02JUL2026-V2
    // Sidebar injection gates were previously coupled — a region
    // needed BOTH features.my_products AND features.campaign_images
    // true for EITHER link to appear. Now split so each link is
    // gated on its own feature flag independently: a region with
    // campaign_images=true but my_products=false will show only the
    // Campaign Images link, and vice-versa.
    const userRegion = (user?.region || '').toLowerCase();
    const regionRow = Array.isArray(activeRegions)
      ? activeRegions.find((r) => (r?.code || '').toLowerCase() === userRegion)
      : null;
    const hasMyProducts = regionRow?.features?.my_products === true;
    const hasCampaignImages = regionRow?.features?.campaign_images === true;
    if (hasMyProducts || hasCampaignImages) {
      navGroups = distributorNav.map((g) => {
        if (g.group !== 'Core') return g;
        const items = [];
        for (const item of g.items) {
          items.push(item);
          if (item.path === '/dashboard/catalogue') {
            if (hasMyProducts) {
              items.push({
                label: 'My Products',
                path:  '/dashboard/my-products',
                icon:  Heart,
              });
            }
            // ceo_instruction_ref: CAMPAIGN-IMAGES-30JUN2026-STAGE1
            // ceo_instruction_ref: CAMPAIGN-IMAGES-ALL-DISTRIBUTORS-ROLLOUT-02JUL2026-V2
            if (hasCampaignImages) {
              items.push({
                label: 'Campaign Images',
                path:  '/dashboard/campaign-images',
                icon:  ImageIcon,
              });
            }
          }
        }
        return { ...g, items };
      });
    } else {
      navGroups = distributorNav;
    }
    // ceo_instruction_ref: kenya_pack_gating_23jul2026 — the static
    // four-tier Distributor Packs pages are UK-only; hide the link
    // for every non-UK viewer (region-driven).
    if (userRegion && userRegion !== 'uk' && userRegion !== 'gb') {
      navGroups = navGroups.map((g) => g.group === 'Tools'
        ? { ...g, items: g.items.filter((i) => i.path !== '/dashboard/distributor-packs') }
        : g);
    }
    // ceo_instruction_ref: kenya_factory_direct_build3_24jul2026 —
    // Kenya distributors get the read-only factory-order tracker.
    if (userRegion === 'kenya') {
      navGroups = navGroups.map((g) => g.group === 'Core'
        ? { ...g, items: [...g.items, { label: 'My Factory Orders', path: '/dashboard/kenya-orders', icon: Factory }] }
        : g);
    }
    // ceo_instruction_ref: kenya_cart_gating_quote_only_24jul2026 —
    // quote-only distributors never see a Cart entry (top level or
    // nested children).
    if (isQuoteOnlyDistributor(user)) {
      navGroups = navGroups.map((g) => ({
        ...g,
        items: g.items
          .filter((i) => i.path !== '/dashboard/cart')
          .map((i) => Array.isArray(i.children)
            ? { ...i, children: i.children.filter((c) => c.path !== '/dashboard/cart') }
            : i),
      }));
    }
  } else if (role === 'marketing_lead') {
    navPrimary = [
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      { label: 'Newsletter', path: '/dashboard/marketing/newsletter', icon: FileText },
      { label: 'Products', path: '/dashboard/catalogue', icon: Package },
      { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
    ];
    navMoreItems = [];
  } else if (isTeamExperience) {
    // team_experience_26oct2026 — owner tick boxes decide which pages show
    const a = teamAccess.areas;
    const extras = [
      a.access_quotes && { label: 'Quotes', path: '/dashboard/my-salon-quotes', icon: FileText },
      a.access_invoices_payments && { label: 'Invoices', path: '/dashboard/my-salon-invoices', icon: CreditCard },
      a.access_team && { label: 'My Team', path: '/dashboard/my-team', icon: Users },
    ].filter(Boolean);
    navGroups = teamMemberNav.map((g) => ({
      ...g,
      items: [...g.items.filter((it) => {
        if (it.path === '/dashboard/catalogue') return a.access_products;
        if (it.path === '/dashboard/cart') return a.access_place_orders;
        if (it.path === '/dashboard/orders') return a.access_orders;
        if (it.path === '/dashboard/learning') return a.access_academy;
        return true;
      }), ...extras],
    }));
  } else if (role === 'agent') {
    // Phase 3 agent nav — mirrors the sales_rep nav item for item,
    // substituting agent equivalents (her dashboard, her commissions)
    // and withholding buying, rep-commission and staff surfaces.
    // ceo_instruction_ref: agent_rep_phase3_full_parity_01aug2026
    navPrimary = [
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      { label: 'My Salons', path: '/dashboard/salon-pipeline', icon: Store },
      { label: 'Invite a Salon', path: '/dashboard/onboard', icon: UserPlus },
      { label: 'My Clients', path: '/dashboard/salon-registry', icon: Users },
      { label: 'Quotes', path: '/dashboard/quotes', icon: FileText },
      { label: 'Lookbook', path: '/dashboard/lookbook', icon: BookOpen },
      { label: 'Learning', path: '/dashboard/learning', icon: GraduationCap },
      { label: 'Products', path: '/dashboard/catalogue', icon: Package },
      { label: 'My Commissions', path: '/dashboard/agent-commissions', icon: Percent },
      { label: 'Profile', path: '/dashboard/profile', icon: User },
    ];
    navMoreItems = [
      { label: 'Barcode Scanner', path: '/dashboard/barcode', icon: ScanLine },
      { label: 'Sales Funnel', path: '/dashboard/sales-funnel', icon: Filter },
      { label: 'Share Pack', path: '/dashboard/share-pack', icon: Share2 },
      { label: 'Leads', path: '/dashboard/leads', icon: UserPlus },
      { label: 'Visit Tracking', path: '/dashboard/visits', icon: ClipboardCheck },
      { label: 'Business Planner', path: '/dashboard/business-planner', icon: BarChart3 },
      { label: 'Calculator', path: '/dashboard/calculator', icon: Calculator },
      { label: 'Campaign Images', path: '/dashboard/campaign-images', icon: ImageIcon },
      { label: 'Professionals', path: '/dashboard/salon-directory', icon: Globe },
      { label: 'Stock Check', path: '/dashboard/stock-check-rep', icon: ClipboardCheck },
      { label: 'Notifications', path: '/dashboard/messages', icon: Bell },
      { label: 'FAQ', path: '/dashboard/faq', icon: FileQuestion },
      { label: 'Help & Support', path: '/dashboard/help', icon: LifeBuoy },
    ];
  } else if (['salon', 'salon_owner', 'salon_manager', 'salon_staff', 'professional', 'salon_business', 'professional_hairdresser'].includes(role)) {
    navPrimary = salonPrimary;
    navMoreItems = salonMore;
  } else {
    navPrimary = salonPrimary;
    navMoreItems = salonMore;
  }

  // Apply visibility filters for demo users
  if (role === 'demo_user' && demoVisibility) {
    const VIS_PATH_MAP = {
      '/dashboard': 'dashboard',
      '/dashboard/catalogue': 'products',
      '/dashboard/education': 'training',
      '/dashboard/results-logbook': 'results',
      '/dashboard/before-after': 'results',
      '/dashboard/my-salon-orders': 'orders',
      '/dashboard/orders': 'orders',
      '/dashboard/my-salon-invoices': 'invoices',
      '/dashboard/my-salon-quotes': 'quotes',
      '/dashboard/quotes': 'quotes',
      '/dashboard/starter-packs': 'starter_packs',
      '/dashboard/calculator': 'calculator',
      '/dashboard/faq': 'faq',
      '/dashboard/help': 'faq',
      '/dashboard/cost-dashboard': 'cost_data',
      '/dashboard/sales-analytics': 'reports',
      '/dashboard/ceo-command': 'reports',
      '/dashboard/ceo-digest': 'reports',
      '/dashboard/inventory': 'stock',
      '/dashboard/stock': 'stock',
      '/dashboard/inventory-intelligence': 'stock',
    };
    navGroups = navGroups.map(g => ({
      ...g,
      items: g.items.filter(item => {
        const visKey = VIS_PATH_MAP[item.path];
        if (!visKey) return true;
        return demoVisibility[visKey] !== false;
      })
    })).filter(g => g.items.length > 0);
  }

  // Delivery Agent Route Guard
  const DELIVERY_ALLOWED = ['/dashboard', '/dashboard/delivery', '/dashboard/messages', '/dashboard/faq', '/dashboard/help', '/dashboard/profile'];
  const isDeliveryAgent = role === 'delivery_agent';
  // ceo_instruction_ref: kenya_cart_gating_quote_only_24jul2026
  const quoteOnlyDistributor = isQuoteOnlyDistributor(user);
  useEffect(() => {
    if (isDeliveryAgent && !DELIVERY_ALLOWED.includes(location.pathname)) {
      navigate('/dashboard', { replace: true });
    }
  }, [location.pathname, isDeliveryAgent, navigate]);

  // Phase 3 agent guard — agents never reach buying, rep-commission,
  // receipts or stock-transfer surfaces, even by direct URL.
  // ceo_instruction_ref: agent_rep_phase3_full_parity_01aug2026
  const AGENT_BLOCKED = [
    '/dashboard/cart', '/dashboard/quick-order', '/dashboard/orders',
    '/dashboard/starter-packs', '/dashboard/reorder', '/dashboard/reorder-intelligence',
    '/dashboard/product-scanner', '/dashboard/finish-product', '/finish-product',
    '/dashboard/my-payroll', '/dashboard/payroll', '/dashboard/my-invoices',
    '/dashboard/my-receipts', '/dashboard/transfer-requests', '/dashboard/stock-transfers',
  ];
  const isAgentRole = role === 'agent';
  useEffect(() => {
    if (isAgentRole && AGENT_BLOCKED.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
      navigate('/dashboard', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAgentRole, navigate]);

  // Warehouse Admin Route Guard (Jane Beeston — Phase 1 RBAC lockdown 21 Apr 2026).
  // UK-only, dispatch/inventory operations only. Every commercial route must
  // 302 back to /dashboard/warehouse. NO EUR, NO cost pricing, NO Nigeria data,
  // NO revenue, NO invoices, NO finance.
  const WAREHOUSE_ADMIN_ALLOWED = [
    '/dashboard/warehouse',
    '/dashboard/dispatch-history',
    '/dashboard/inventory',
    '/dashboard/stock-check',
    '/dashboard/manual-stock-check',
    '/dashboard/barcode',
    '/dashboard/product-scanner',
    '/dashboard/messages',
    '/dashboard/profile',
    '/dashboard/security',
    '/dashboard/faq',
    '/dashboard/help',
  ];
  const isWarehouseAdminRole = role === 'warehouse_admin';
  useEffect(() => {
    if (!isWarehouseAdminRole) return;
    const path = location.pathname;
    // Jane's landing page is /dashboard/warehouse, not /dashboard
    if (path === '/dashboard') {
      navigate('/dashboard/warehouse', { replace: true });
      return;
    }
    const allowed = WAREHOUSE_ADMIN_ALLOWED.some(
      (p) => path === p || path.startsWith(p + '/')
    );
    if (!allowed) {
      navigate('/dashboard/warehouse', { replace: true });
    }
  }, [location.pathname, isWarehouseAdminRole, navigate]);

  // Salon Role Guard — lock out of admin/distributor routes
  const SALON_ALLOWED = [
    '/dashboard', '/dashboard/catalogue', '/dashboard/profile',
    '/dashboard/complete-details',
    '/dashboard/my-salon-orders', '/dashboard/my-salon-invoices', '/dashboard/my-salon-quotes',
    '/dashboard/invoices',
    '/dashboard/messages', '/dashboard/faq', '/dashboard/help',
    '/dashboard/education', '/dashboard/learning', '/dashboard/results-logbook', '/dashboard/results-gallery',
    '/dashboard/before-after',
    '/dashboard/reorder-intelligence',
    '/dashboard/starter-packs', '/dashboard/calculator', '/dashboard/training',
    '/dashboard/product-scanner', '/dashboard/gallery', '/dashboard/rewards',
    '/dashboard/verification-hub', '/dashboard/lookbook', '/dashboard/salon-settings',
    '/dashboard/salon-search', '/dashboard/salon-directory', '/dashboard/about',
    '/dashboard/protocols', '/dashboard/business-planner', '/dashboard/reorder',
    '/dashboard/security', '/dashboard/subscriptions', '/dashboard/training-module',
    '/dashboard/results-logbook', '/dashboard/cart', '/dashboard/stock-check-rep',
    '/dashboard/my-team',
  ];
  const isSalonUser = ['salon', 'salon_owner', 'salon_manager', 'salon_staff', 'professional', 'salon_business', 'professional_hairdresser'].includes(role);
  const isDemoSalon = role === 'demo_user' && ['salon', 'visitor', 'professional_hairdresser', 'trainer'].includes(demoViewAs);
  useEffect(() => {
    if ((isSalonUser || isDemoSalon) && !SALON_ALLOWED.includes(location.pathname) && !location.pathname.startsWith('/dashboard/booking/') && !location.pathname.startsWith('/dashboard/catalogue/') && !location.pathname.startsWith('/dashboard/education/') && !location.pathname.startsWith('/dashboard/results/') && !location.pathname.startsWith('/dashboard/protocols/') && !location.pathname.startsWith('/dashboard/salon-pipeline/') && !location.pathname.startsWith('/dashboard/starter-packs/')) {
      navigate('/dashboard', { replace: true });
    }
  }, [location.pathname, isSalonUser, isDemoSalon, navigate]);

  /* voice_every_section_jun2026 — every card/section on every salon page gets its
     own 🔊 Listen (and 🎤 Speak where it has boxes), same as the booking platform. */
  useEffect(() => {
    if (!(isSalonUser || isTeamExperience || isDemoSalon)) return undefined;
    return watchSections(document.getElementById('rvk-main-content'), RVK_SV_CARDS);
  }, [location.pathname, isSalonUser, isTeamExperience, isDemoSalon]);

  // ─── Scroll Position Persistence ───
  useEffect(() => {
    // Restore scroll position when navigating back
    const saved = sessionStorage.getItem(`scroll_${location.pathname}`);
    if (saved) {
      const pos = parseInt(saved, 10);
      sessionStorage.removeItem(`scroll_${location.pathname}`);
      // Use multiple frames to ensure DOM has rendered
      const timer = setTimeout(() => window.scrollTo(0, pos), 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Save scroll on unmount
  useEffect(() => {
    return () => {
      sessionStorage.setItem(`scroll_${location.pathname}`, window.scrollY.toString());
    };
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  /* design_lock navigation — max 5 big buttons (picture + one word) for the
     portal roles; founder/admin keep the full admin sidebar. */
  const usesPillNav = isSalonUser || isTeamExperience
    || ['distributor', 'sales_rep', 'agent', 'warehouse_admin', 'delivery_agent', 'educator'].includes(role);
  /* CEO role suites 27 Oct 2026 — exact bars from the design file */
  const SUITE_NAV = {
    sales_rep: {
      // rep_menu_approved_29oct2026 (CEO rep menu.pdf): bar reads exactly
      // Home · Orders · Dispatch · Academy · More.
      prim: [
        { label: 'Home', path: '/dashboard', icon: LayoutDashboard },
        { label: 'Orders', path: '/dashboard/rep-suite-orders', icon: ClipboardList },
        { label: 'Dispatch', path: '/dashboard/orders', icon: Truck },
        { label: 'Academy', path: '/dashboard/learning', icon: GraduationCap },
      ],
      more: [
        // More lists every remaining page the rep is allowed.
        { label: 'Quick order', path: '/dashboard/rep-quick', icon: RvkZap },
        { label: 'My salons', path: '/dashboard/rep-salons', icon: Users },
        { label: 'Invoices', path: '/dashboard/my-invoices', icon: Receipt },
        { label: 'Receipts', path: '/dashboard/my-receipts', icon: CreditCard },
        { label: 'Record payment', path: '/dashboard/ng-invoices', icon: Banknote },
        { label: 'Stock', path: '/dashboard/stock-check-rep', icon: ClipboardCheck },
        { label: 'Leads', path: '/dashboard/rep-leads', icon: FileText },
        { label: 'Commission', path: '/dashboard/rep-commission', icon: CreditCard },
      ],
    },
    agent: {
      prim: [
        { label: 'Home', path: '/dashboard', icon: LayoutDashboard },
        { label: 'My clients', path: '/dashboard/agent-clients', icon: Users },
        { label: 'New order', path: '/dashboard/agent-new-order', icon: Package },
        { label: 'Commission', path: '/dashboard/agent-suite-commission', icon: CreditCard },
      ],
      more: [
        { label: 'Orders', path: '/dashboard/agent-suite-orders', icon: ClipboardList },
        { label: 'Academy', path: '/dashboard/learning', icon: GraduationCap },
      ],
    },
    distributor: {
      prim: [
        { label: 'Home', path: '/dashboard', icon: LayoutDashboard },
        { label: 'Place order', path: '/dashboard/dist-place-order', icon: Package },
        { label: 'Orders', path: '/dashboard/dist-orders', icon: ClipboardList },
        { label: 'Invoices', path: '/dashboard/dist-invoices', icon: FileText },
      ],
      more: [
        { label: 'My salons', path: '/dashboard/dist-salons', icon: Users },
        { label: 'Academy', path: '/dashboard/learning', icon: GraduationCap },
      ],
    },
    educator: {
      prim: [
        { label: 'Home', path: '/dashboard', icon: LayoutDashboard },
        { label: 'Calendar', path: '/dashboard/edu-calendar', icon: RvkCalendar },
        { label: 'Live class', path: '/dashboard/edu-live', icon: RvkRadio },
        { label: 'Ask', path: '/dashboard/edu-ask', icon: RvkMic },
      ],
      more: [
        { label: 'Requests', path: '/dashboard/edu-requests', icon: RvkInbox },
        { label: 'Students', path: '/dashboard/edu-students', icon: Users },
        { label: 'Academy', path: '/dashboard/learning', icon: GraduationCap },
      ],
    },
  };
  /* ceo_bottom_bar_27oct2026 — one truth for when the plum bottom bar shows */
  const hasBottomBar = isSalonUser || isTeamExperience || !!SUITE_NAV[role];
  let pillPrimary = [];
  let pillMore = [];
  if (SUITE_NAV[role]) {
    pillPrimary = SUITE_NAV[role].prim;
    pillMore = SUITE_NAV[role].more;
  } else if (usesPillNav) {
    const navSource = (isSalonUser && !isTeamExperience)
      ? salonGroupedNav
      : (navPrimary.length ? navPrimary : navGroups.flatMap((g) => (g && g.items) || []));
    const flatNav = navSource.filter(Boolean).flatMap((i) => [i, ...(Array.isArray(i.children) ? i.children.filter(Boolean) : [])]);
    if (isTeamExperience) {
      const a = teamAccess.areas;
      pillPrimary = [
        { label: 'Home', path: '/dashboard', icon: LayoutDashboard },
        a.access_products && { label: 'Products', path: '/dashboard/catalogue', icon: Package },
        a.access_orders && { label: 'Orders', path: '/dashboard/orders', icon: ClipboardList },
        a.access_academy && { label: 'Academy', path: '/dashboard/learning', icon: GraduationCap },
      ].filter(Boolean);
    } else if (isSalonUser) {
      pillPrimary = [
        { label: 'Home', path: '/dashboard', icon: LayoutDashboard },
        { label: 'Products', path: '/dashboard/catalogue', icon: Package },
        { label: 'Orders', path: '/dashboard/my-salon-orders', icon: ClipboardList },
        { label: 'Academy', path: '/dashboard/learning', icon: GraduationCap },
      ];
    } else {
      const LABEL_MAP = {
        'Dispatch': 'Dispatch', 'Dispatch History': 'History', 'My Salons': 'Salons',
        'Invite a Salon': 'Onboard', 'My Commissions': 'Earnings', 'Quick Order': 'Order',
        'My Deliveries': 'Deliveries', 'My Orders': 'Orders', 'My Products': 'Products',
      };
      const used = new Set(['Home', 'More']);
      const oneWord = (l) => {
        let w = LABEL_MAP[l] || (l || '').replace(/^(My|Invite a|Place|Your)\s+/i, '').split(' ')[0];
        if (used.has(w)) w = (l || '').split(' ').pop();
        used.add(w);
        return w;
      };
      const home = flatNav.find((i) => i.path === '/dashboard') || { label: 'Home', path: '/dashboard', icon: LayoutDashboard };
      pillPrimary = [{ ...home, label: 'Home' },
        ...flatNav.filter((i) => i.path !== '/dashboard').slice(0, 3).map((i) => ({ ...i, label: oneWord(i.label) }))];
    }
    const seen = new Set(pillPrimary.map((i) => i.path).concat('/dashboard'));
    pillMore = [...flatNav, ...navMoreItems].filter((i) => {
      if (!i || !i.path || seen.has(i.path)) return false;
      seen.add(i.path);
      return true;
    });
  }

  /* details_gate_27oct2026 (CEO) — HARD gate: any account with missing
     details sees only the Complete your details page. No skip. */
  if (user?.profile_incomplete && !user?.must_change_password) {
    return <CompleteProfilePage />;
  }

  return (
    <div className={`min-h-screen bg-[#F4F8FC] notranslate ${luxeShell ? 'luxe' : ''} ${isSalonRole ? 'rvk-reskin' : ''}`} translate="no" data-testid="dashboard-layout">
      {/* Impersonation Banner */}
      <ImpersonationBanner />

      {/* Tablet & Mobile Header — sits BELOW impersonation banner when present.
          Drawer-mode applies up to `xl` (1280px) so iPad portrait + landscape both
          get a dismissible sidebar (bug fix 18 Apr 2026 — iPad sidebar stayed pinned). */}
      <div
        className={`xl:hidden fixed left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[#E3EAF3] transition-transform duration-200 ${impersonation ? 'top-10' : 'top-0'} ${(barsHidden && !sidebarOpen && !searchOpen) || location.pathname.startsWith('/dashboard/booking/cal') ? '-translate-y-full' : ''}`}
        data-testid="mobile-topbar"
      >
        <div className="px-2 h-[50px] flex items-center gap-1">
          <button
            onClick={() => {
              /* fix 2 (Jun 2026): the ☰ first closes any open box inside the
                 booking app, then opens the one menu — first tap, every page */
              try {
                const f = document.querySelector('iframe[data-testid=booking-embed-frame]');
                if (f && f.contentWindow) f.contentWindow.postMessage({ rvk: 'closeBoxes' }, '*');
              } catch { /* no embed on this page */ }
              setSidebarOpen(!sidebarOpen);
            }}
            className="w-11 h-11 flex items-center justify-center rounded-xl active:scale-95 transition-all"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {sidebarOpen
              ? <X className="w-6 h-6 text-[#2B1F27]" />
              : <Menu className="w-6 h-6 text-[#2B1F27]" />}
          </button>
          <img src="https://customer-assets.emergentagent.com/job_distribution-pro-1/artifacts/44tlre5m_IMG_2568.png" alt="Revitalise Keratin" className="h-7 w-auto object-contain max-w-[120px] mr-auto" data-testid="mobile-logo" />
          <button onClick={barListen} aria-label={barSpeak === 'on' ? 'Pause reading' : 'Listen to this page'}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-[17px]" data-testid="topbar-listen">
            {barSpeak === 'on' ? '⏸' : barSpeak === 'paused' ? '▶' : '🔊'}
          </button>
          {barSpeak !== 'off' && (
            <button onClick={barStop} aria-label="Stop reading" className="w-10 h-10 flex items-center justify-center rounded-xl text-[15px]" data-testid="topbar-stop">⏹</button>
          )}
          <button onClick={cycleAa} aria-label="Text size" title="Text size"
            className={`w-10 h-10 flex items-center justify-center rounded-xl font-semibold text-[15px] ${aa ? 'bg-[#F1DCE2]' : ''}`}
            data-testid="topbar-text-size">Aa</button>
          <button onClick={() => setSearchOpen((v) => !v)} aria-label="Search" aria-expanded={searchOpen}
            className={`w-10 h-10 flex items-center justify-center rounded-xl ${searchOpen ? 'bg-[#F4F8FC]' : ''}`} data-testid="topbar-search-toggle">
            <Search className="w-5 h-5 text-[#2B1F27]" />
          </button>
          <NotificationBell testId="notification-bell-mobile" />
          {!isDeliveryAgent && !quoteOnlyDistributor && (
            <Link to="/dashboard/cart" className="relative w-10 h-10 flex items-center justify-center" data-testid="mobile-cart-link" aria-label="Basket">
              <ShoppingCart className="w-5 h-5" />
              {totalItems > 0 && (
                <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full" data-testid="mobile-cart-badge">{totalItems}</span>
              )}
            </Link>
          )}
        </div>
        {/* Search opens only when 🔍 is tapped — box plus Recent chips under the bar */}
        {searchOpen && (
          <div className="px-3 pb-2 pt-1 border-t border-[#E3EAF3]/60" data-testid="mobile-search-panel">
            <GlobalSearch autoFocus />
            <RecentlyVisitedBar />
          </div>
        )}
      </div>

      {/* Sidebar — salon roles get the CEO-approved plum reskin
          (menu_reskin_01nov2026); every other role keeps the white aside. */}
      {usesAccordionNav ? (
        <SalonLuxeMenu open={sidebarOpen} onClose={() => setSidebarOpen(false)}
          menu={oneMenu} onLogout={handleLogout} />
      ) : (
      <aside className={`fixed top-0 left-0 z-[85] h-[100dvh] w-64 bg-white border-r border-[#E3EAF3] shadow-sm transform transition-transform ${usesPillNav ? (sidebarOpen ? 'xl:translate-x-0' : 'xl:-translate-x-full') : 'xl:translate-x-0'} flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="sidebar">
        {/* Brand */}
        <div className="px-5 py-6 border-b border-[#E3EAF3] flex items-center justify-between flex-shrink-0">
          <img
            src="https://customer-assets.emergentagent.com/job_distribution-pro-1/artifacts/44tlre5m_IMG_2568.png"
            alt="Revitalise Keratin"
            className="w-auto h-auto object-contain"
            style={{ maxHeight: '56px', maxWidth: '190px' }}
            data-testid="sidebar-logo"
          />
          <div className="flex items-center gap-2">
            <div className="hidden xl:block">
              <NotificationBell testId="notification-bell-sidebar" />
            </div>
            <button type="button" aria-label="Close menu" data-testid="drawer-close-btn"
              onClick={() => setSidebarOpen(false)}
              className="xl:hidden w-11 h-11 flex items-center justify-center rounded-xl border border-[#E3EAF3] text-[#2B1F27] bg-[#F4F8FC]">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Grouped Navigation */}
        <nav className="p-3 flex-1 overflow-y-auto scrollbar-thin" data-testid="sidebar-nav">
          {role === 'finance_director' ? (
            <>
              <FinanceDirectorTabNav location={location} setSidebarOpen={setSidebarOpen} />
              {/* ceo_instruction_ref: CAMPAIGN-IMAGES-SIDEBAR-FD-01JUL2026
                  — Campaign Images standalone link for finance_director
                  role only, rendered below the Nicky-dashboard tab nav
                  so she can reach the page without typing the URL. */}
              <div className="mt-3 pt-3 border-t border-[#E3EAF3]">
                <NavItem
                  item={{ label: 'Campaign Images', path: '/dashboard/campaign-images', icon: ImageIcon }}
                  location={location}
                  totalItems={totalItems}
                  setSidebarOpen={setSidebarOpen}
                />
              </div>
            </>
          ) : usesAccordionNav ? (
            <div className="space-y-0.5">
              {salonNavWithBooking.map(group => (
                <AccordionNavGroup key={group.label} group={group} location={location} totalItems={totalItems} setSidebarOpen={setSidebarOpen} />
              ))}
            </div>
          ) : usesPrimaryMore ? (
            <>
              <div className="space-y-0.5">
                {navPrimary.map(item => (
                  <NavItem key={item.path} item={item} location={location} totalItems={totalItems} setSidebarOpen={setSidebarOpen} />
                ))}
              </div>
              {navMoreItems.length > 0 && (
                <MoreSection items={navMoreItems} location={location} totalItems={totalItems} setSidebarOpen={setSidebarOpen} />
              )}
            </>
          ) : (
            navGroups.map((group, gi) => (
              <NavGroup key={gi} group={group.group} items={group.items}
                location={location} totalItems={totalItems} setSidebarOpen={setSidebarOpen} />
            ))
          )}
        </nav>

        {/* Footer Icon Row — salon users only */}
        {usesAccordionNav && (
          <FooterIconRow items={salonFooterItems} location={location} setSidebarOpen={setSidebarOpen} />
        )}

        {/* User Info */}
        <div className="p-4 border-t border-[#E3EAF3] flex-shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#B99A65]/10 flex items-center justify-center overflow-hidden border border-gray-200 shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover rounded-full" />
              ) : (
                <span className="text-[#B99A65] font-semibold text-sm">{user?.name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-[#2B1F27]">{user?.name}</p>
              <p className="text-[11px] text-[#64525D] capitalize">{
                role === 'founder' ? 'Founder & CEO' :
                role === 'team_member' ? 'Team Member' :
                role === 'super_admin' ? 'CEO / Super Admin' :
                role === 'admin_manager' ? 'Admin Manager' :
                role === 'warehouse_admin' ? 'Warehouse Admin' :
                role === 'sales_rep' ? 'Sales Rep' :
                role === 'agent' ? 'Agent' :
                role === 'salon_staff' ? 'Salon Staff' :
                role === 'delivery_agent' ? 'Delivery Agent' :
                role === 'marketing_lead' ? 'Marketing Lead' :
                role === 'finance_director' ? 'Finance Director' :
                role.replace('_', ' ')
              }</p>
            </div>
          </div>
          {!usesAccordionNav && (
            <Link to="/dashboard/profile"
              className="flex items-center gap-2 w-full h-8 px-3 mb-2 rounded-lg text-xs uppercase tracking-wider border border-[#E3EAF3] text-[#B99A65] hover:text-[#2B1F27] hover:bg-[#E6F0FA] transition-colors"
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-my-profile">
              <User className="w-3 h-3" /> My Profile
            </Link>
          )}
          <Link to="/dashboard/security"
            className="flex items-center gap-2 w-full h-8 px-3 mb-2 rounded-lg text-xs uppercase tracking-wider border border-[#E3EAF3] text-[#64525D] hover:text-[#2B1F27] hover:bg-[#E6F0FA] transition-colors"
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-security-settings">
            <Shield className="w-3 h-3" /> Security Settings
          </Link>
          {isSalonRole && (
            <Link to="/dashboard/salon-settings"
              className="flex items-center gap-2 w-full h-8 px-3 mb-2 rounded-lg text-xs uppercase tracking-wider border border-[#E3EAF3] text-[#64525D] hover:text-[#2B1F27] hover:bg-[#E6F0FA] transition-colors"
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-salon-settings">
              <Store className="w-3 h-3" /> Salon Profile
            </Link>
          )}
          {role === 'founder' && <CeoSalonSwitch />}
          <CeoAccountSwitch />
          <ViewAsSelector />
          <Button variant="outline" size="sm" onClick={handleLogout}
            className="w-full min-h-[48px] rounded-full text-sm font-semibold border-[#7A3E22] text-[#7A3E22] bg-white hover:bg-[#F4E8E0] hover:text-[#7A3E22]"
            data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>
      )}

      {/* Overlay — click-outside to close drawer. Below xl for sidebar roles;
          ALL sizes for pill-nav roles (desktop drawer, rep_menu_29oct2026).
          Salon roles: SalonLuxeMenu renders its own plum scrim. */}
      {sidebarOpen && !usesAccordionNav && (
        <div className={`fixed inset-0 z-[80] bg-black/20 ${usesPillNav ? '' : 'xl:hidden'}`} onClick={() => setSidebarOpen(false)} data-testid="drawer-overlay" />
      )}

      {/* Main Content */}
      <main className={`${usesPillNav ? '' : 'xl:ml-64'} min-h-screen rvk-phone-main ${impersonation ? 'pt-[92px] xl:pt-10' : location.pathname.startsWith('/dashboard/booking/cal') ? 'pt-0 xl:pt-0' : 'pt-[52px] xl:pt-0'} ${hasBottomBar ? 'pb-44 xl:pb-0' : ''}`}>
        <CeoAccountBar />
        {/* Desktop Header with Global Search */}
        <div className="hidden xl:flex h-14 bg-white border-b border-[#E3EAF3] px-6 items-center justify-between sticky top-0 z-30" data-testid="desktop-header">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* rep_menu_29oct2026 (CEO): the menu button is back on desktop
                for every pill-nav role — opens the full role drawer. */}
            {usesPillNav && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F4F8FC] border border-[#E3EAF3] shadow-sm active:scale-95 transition-all hover:bg-[#EAF0F7] shrink-0"
                data-testid="desktop-menu-toggle"
                aria-label="Toggle menu"
              >
                {sidebarOpen ? <X className="w-5 h-5 text-[#2B1F27]" /> : <Menu className="w-5 h-5 text-[#2B1F27]" />}
              </button>
            )}
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={cycleAa} aria-label="Text size" title="Text size"
              className={`w-9 h-9 flex items-center justify-center rounded-xl font-semibold ${aa ? 'bg-[#F1DCE2] text-[#291525]' : 'text-[#2B1F27]'}`}
              data-testid="topbar-text-size-desktop">Aa</button>
            {role !== 'finance_director' && <RegionSwitcher />}
            <NotificationBell />
            {!isDeliveryAgent && !quoteOnlyDistributor && (
              <Link to="/dashboard/cart" className="relative" data-testid="desktop-cart-link">
                <ShoppingCart className="w-5 h-5 text-[#4A4A4A]" />
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#B99A65] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full animate-in zoom-in duration-200" data-testid="desktop-cart-badge">{totalItems}</span>
                )}
              </Link>
            )}
            <div className="w-8 h-8 rounded-full bg-[#B99A65]/10 flex items-center justify-center overflow-hidden border border-gray-200 shrink-0 cursor-pointer"
              onClick={() => navigate('/dashboard/profile')} data-testid="header-avatar">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover rounded-full" />
              ) : (
                <span className="text-[#B99A65] font-semibold text-xs">{user?.name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
          </div>
        </div>

        <div id="rvk-main-content" className="px-4 sm:px-6 py-6 max-w-7xl mx-auto w-full">
          {usesPillNav && (
            <div className="hidden xl:block" data-testid="desktop-pill-nav-wrap">
              <TopPillNav primary={pillPrimary} more={pillMore} />
            </div>
          )}
          {role !== 'delivery_agent' && <div className="hidden xl:block"><RecentlyVisitedBar /></div>}
          {/* Demo → Membership CTA banner */}
          {role === 'demo_user' && localStorage.getItem('rk_wants_membership') === 'true' && (
            <div className="mx-4 mb-4 p-4 bg-gradient-to-r from-[#B99A65]/10 to-[#B99A65]/5 border border-[#B99A65]/30 rounded-xl flex items-center justify-between" data-testid="membership-cta-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#B99A65] rounded-lg flex items-center justify-center">
                  <span className="text-white text-lg">&#9733;</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#2B1F27]">Ready to join Revitalise Keratin?</p>
                  <p className="text-xs text-[#64525D]">Complete your membership application — your details are pre-filled.</p>
                </div>
              </div>
              <button
                onClick={() => { window.location.href = '/register/salon?from=demo'; }}
                className="px-4 py-2 bg-[#B99A65] text-white text-xs font-semibold rounded-lg hover:bg-[#B08C3E] transition-colors whitespace-nowrap"
                data-testid="complete-membership-btn"
              >
                Complete Your Registration
              </button>
            </div>
          )}
          {/* business_name_eyebrow_02nov2026 (CEO item 3) — the salon name
              sits over every page heading for salon owners and professionals */}
          {isSalonRole && oneMenu?.user?.salon_name
            && !location.pathname.startsWith('/dashboard/booking') && (
            <p className="luxe-eyebrow" data-testid="business-name-eyebrow"
              style={{ fontSize: 13, margin: '0 0 10px' }}>
              {oneMenu.user.salon_name}
            </p>
          )}
          <ErrorBoundary
            fallback={(err, reset) => (
              <div className="p-6" data-testid="dashboard-content-error">
                <div className="max-w-lg mx-auto bg-white border border-[#E3E0D8] rounded-xl p-5">
                  <h3 className="font-semibold text-[#2B1F27] mb-1">This section could not be displayed</h3>
                  <p className="text-sm text-[#64525D] mb-3">The rest of the dashboard is unaffected, and the fault has been reported automatically.</p>
                  <pre className="text-[10px] text-[#64525D] whitespace-pre-wrap break-words max-h-32 overflow-auto mb-3">{String(err?.message || err)}</pre>
                  <button className="text-xs font-semibold px-3 py-1.5 rounded bg-[#B99A65] text-white" onClick={reset} data-testid="dashboard-content-error-retry">Try again</button>
                </div>
              </div>
            )}
          >
            {/* create-order carries its own in-header Listen controls
                (CEO replacement.html) — no second bar on that page. */}
            {location.pathname !== '/dashboard/create-order' && <ListenBar />}
            {(() => {
              // team_experience_26oct2026 — direct-URL guard for gated pages
              if (isTeamExperience && teamAccess.loaded) {
                const gated = [
                  ['/dashboard/catalogue', 'access_products'],
                  ['/dashboard/cart', 'access_place_orders'],
                  ['/dashboard/orders', 'access_orders'],
                  ['/dashboard/learning', 'access_academy'],
                  ['/dashboard/my-salon-quotes', 'access_quotes'],
                  ['/dashboard/my-salon-invoices', 'access_invoices_payments'],
                  ['/dashboard/my-team', 'access_team'],
                ].find(([p]) => location.pathname.startsWith(p));
                if (gated && !teamAccess.areas[gated[1]]) {
                  return (
                    <div className="max-w-xl mx-auto mt-16 bg-[#FFFFFF] border border-[#D9BE8E]/40 p-10 text-center" data-testid="area-access-blocked">
                      <h2 className="text-2xl text-[#291525] mb-2" style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}>This page is switched off for you</h2>
                      <p className="text-sm text-[#64525D]">Your salon owner has turned off access to this page. Ask them to switch it back on — changes apply straight away, no need to log out.</p>
                    </div>
                  );
                }
              }
              return <Outlet />;
            })()}
          </ErrorBoundary>
        </div>
      </main>
      {(isSalonUser || isTeamExperience || !!SUITE_NAV[role]) && (
        <MobileBottomNav role={role} primaryOverride={SUITE_NAV[role]?.prim} moreOverride={SUITE_NAV[role]?.more}
          onOpenMenu={usesAccordionNav ? () => setSidebarOpen(true) : undefined}
          bookingBar={usesAccordionNav && !!oneMenu?.booking} />
      )}
      <ChatWidget raised={hasBottomBar} />
      <BasketPanel />
    </div>
  );
}
